import { mockSuccess } from './shared';
import type { MockRouteEntry } from '../mock-api.types';

type Ticket = {
    id: string;
    reference: string;
    subject: string;
    status: string;
    priority: string;
    source: string;
    handler?: string;
    messages_count?: number;
    created_at?: string;
    description?: string | null;
    category_id?: string | null;
    project_id?: string | null;
    reporter_email?: string | null;
    assignee_user_id?: number | null;
    linked_url?: string | null;
};

type Message = {
    id: string;
    direction: 'in' | 'out' | 'internal';
    body_text: string;
    vetting_status: string;
    created_at?: string;
};

type Watcher = {
    id: string;
    user_id?: number | null;
    email?: string | null;
};

const CATEGORIES = [
    { id: 'cat-1', name: 'Technical', description: 'Product issues', active: true, sort_order: 1 },
    { id: 'cat-2', name: 'Billing', description: 'Invoices and payments', active: true, sort_order: 2 },
];

const PROJECTS = [
    { id: 'proj-1', title: 'Outbox rollout', status: 'active', ticket_count: 2 },
    { id: 'proj-2', title: 'Support backlog', status: 'active', ticket_count: 1 },
];

const AGENTS = [
    { id: 10, email: 'agent.one@ashcorp.xyz', name: 'Agent One' },
    { id: 11, email: 'agent.two@ashcorp.xyz', name: 'Agent Two' },
];

let SUPPORT_SETTINGS = {
    sla_hours: 24,
    default_priority: 'normal',
    chatbot_enabled: true,
    business_hours: 'Mon–Fri 09:00–17:00',
    same_mailbox: true,
    inbound_enabled: false,
    auto_send_public_notes: true,
    inbound_imap_port: 993,
    inbound_imap_encryption: 'ssl',
    outbound_smtp_port: 587,
    outbound_smtp_encryption: 'tls',
};

const TICKETS: Ticket[] = [
    {
        id: 'tkt-1',
        reference: 'SUP-1001',
        subject: 'Cannot export DMS PDF',
        status: 'open',
        priority: 'normal',
        source: 'web',
        handler: 'tenant',
        messages_count: 2,
        created_at: '2026-06-04T09:00:00Z',
        description: 'Export button spins forever when exporting large PDF bundles.',
        category_id: 'cat-1',
        project_id: 'proj-1',
        reporter_email: 'reporter@example.com',
        assignee_user_id: null,
        linked_url: 'https://example.com/docs/export',
    },
    {
        id: 'tkt-2',
        reference: 'SUP-1002',
        subject: 'Billing invoice question',
        status: 'pending_vetting',
        priority: 'low',
        source: 'email',
        handler: 'tenant',
        messages_count: 1,
        created_at: '2026-06-03T14:00:00Z',
        category_id: 'cat-2',
        project_id: 'proj-2',
        reporter_email: 'billing@example.com',
    },
    {
        id: 'tkt-3',
        reference: 'SUP-1003',
        subject: 'Mailing calendar sync',
        status: 'open',
        priority: 'high',
        source: 'chat',
        handler: 'tenant',
        messages_count: 1,
        created_at: '2026-06-04T08:00:00Z',
        category_id: 'cat-1',
        project_id: 'proj-1',
        reporter_email: 'ops@example.com',
    },
];

const MESSAGES: Record<string, Message[]> = {
    'tkt-1': [
        { id: 'm-1', direction: 'in', body_text: 'Export button spins forever.', vetting_status: 'sent', created_at: '2026-06-04T09:00:00Z' },
        { id: 'm-2', direction: 'out', body_text: 'We are checking the mock PDF endpoint.', vetting_status: 'sent', created_at: '2026-06-04T09:30:00Z' },
    ],
    'tkt-3': [
        { id: 'm-4', direction: 'in', body_text: 'Can we see team meetings in mailing calendar?', vetting_status: 'sent', created_at: '2026-06-04T08:00:00Z' },
    ],
    'tkt-2': [
        { id: 'm-3', direction: 'in', body_text: 'Please confirm invoice #4402.', vetting_status: 'pending', created_at: '2026-06-03T14:00:00Z' },
    ],
};

const TIME_ENTRIES: Record<string, { id: string; entry_type: string; hours: number; created_at: string }[]> = {
    'tkt-1': [{ id: 'te-1', entry_type: 'investigation', hours: 1.5, created_at: '2026-06-04T10:00:00Z' }],
};

const ACTIVITIES: Record<string, { id: string; event_type: string; created_at: string }[]> = {
    'tkt-1': [
        { id: 'act-1', event_type: 'ticket.created', created_at: '2026-06-04T09:00:00Z' },
        { id: 'act-2', event_type: 'message.added', created_at: '2026-06-04T09:30:00Z' },
    ],
};

const ATTACHMENTS: Record<string, { id: string; filename: string; size_bytes: number; created_at: string }[]> = {
    'tkt-1': [{ id: 'att-1', filename: 'screenshot.png', size_bytes: 245000, created_at: '2026-06-04T09:05:00Z' }],
};

const WATCHERS: Record<string, Watcher[]> = {
    'tkt-1': [{ id: 'w-1', email: 'watcher@example.com' }],
};

function ticketId(url: string): string | undefined {
    const m = url.match(/\/tickets\/([^/]+)/i);
    return m?.[1];
}

function findTicket(id: string | undefined): Ticket | undefined {
    if (!id) {
        return undefined;
    }
    return TICKETS.find((t) => t.id === id);
}

function ticketDetail(id: string | undefined) {
    const ticket = findTicket(id) ?? TICKETS[0];
    const key = ticket.id;
    return {
        ticket,
        messages: MESSAGES[key] ?? [],
        time_entries: TIME_ENTRIES[key] ?? [],
        activities: ACTIVITIES[key] ?? [],
        attachments: ATTACHMENTS[key] ?? [],
        watchers: WATCHERS[key] ?? [],
    };
}

export const SUPPORT_MOCK_HANDLERS: MockRouteEntry[] = [
    { method: 'GET', pattern: /\/api\/[^/]+\/[^/]+\/support\/settings/i, handler: () => mockSuccess(SUPPORT_SETTINGS) },
    {
        method: 'PATCH',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/settings/i,
        handler: (req) => {
            SUPPORT_SETTINGS = { ...SUPPORT_SETTINGS, ...(req.body as object) };
            return mockSuccess(SUPPORT_SETTINGS);
        },
    },
    { method: 'GET', pattern: /\/api\/[^/]+\/[^/]+\/support\/categories/i, handler: () => mockSuccess({ data: CATEGORIES }) },
    { method: 'GET', pattern: /\/api\/[^/]+\/[^/]+\/support\/projects/i, handler: () => mockSuccess({ data: PROJECTS }) },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/projects(\?|$)/i,
        handler: (req) => {
            const body = (req.body || {}) as {
                title?: string;
                status?: string;
                budget_display?: string;
                project_phase?: string;
                description?: string;
            };
            const project = {
                id: `proj-${Date.now()}`,
                title: `${body.title ?? 'New project'}`.trim() || 'New project',
                status: `${body.status ?? 'draft'}`.trim() || 'draft',
                ticket_count: 0,
            };
            PROJECTS.push(project);
            return mockSuccess(project, 'Support project created.');
        },
    },
    { method: 'GET', pattern: /\/api\/[^/]+\/[^/]+\/support\/agents/i, handler: () => mockSuccess({ data: AGENTS }) },

    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets(\?|$)/i,
        handler: () => mockSuccess({ data: TICKETS }),
    },
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/?]+/i,
        handler: (req) => mockSuccess(ticketDetail(ticketId(req.url))),
    },
    {
        method: 'PATCH',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/?]+$/i,
        handler: (req) => {
            const id = ticketId(req.url) ?? TICKETS[0].id;
            const ticket = findTicket(id);
            if (ticket) {
                Object.assign(ticket, req.body as object);
            }
            return mockSuccess(ticket ?? TICKETS[0]);
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/]+\/messages/i,
        handler: (req) => {
            const id = ticketId(req.url) ?? TICKETS[0].id;
            const body = (req.body || {}) as { body?: string; internal?: boolean };
            const msg: Message = {
                id: `m-${Date.now()}`,
                direction: body.internal ? 'internal' : 'out',
                body_text: body.body || '',
                vetting_status: 'sent',
                created_at: new Date().toISOString(),
            };
            MESSAGES[id] = [...(MESSAGES[id] ?? []), msg];
            return mockSuccess(msg);
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/]+\/assign-to-reporter/i,
        handler: (req) => {
            const id = ticketId(req.url) ?? TICKETS[0].id;
            const ticket = findTicket(id);
            const body = (req.body || {}) as { status?: string; time_spent_hours?: number };
            if (ticket && body.status) {
                ticket.status = body.status;
                ticket.assignee_user_id = null;
            }
            return mockSuccess(ticket ?? TICKETS[0]);
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/]+\/assign-agent/i,
        handler: (req) => {
            const id = ticketId(req.url) ?? TICKETS[0].id;
            const ticket = findTicket(id);
            const body = (req.body || {}) as { assignee_user_id?: number; status?: string };
            if (ticket) {
                if (body.assignee_user_id != null) {
                    ticket.assignee_user_id = body.assignee_user_id;
                }
                if (body.status) {
                    ticket.status = body.status;
                }
            }
            return mockSuccess(ticket ?? TICKETS[0]);
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/]+\/change-status/i,
        handler: (req) => {
            const id = ticketId(req.url) ?? TICKETS[0].id;
            const ticket = findTicket(id);
            const body = (req.body || {}) as { status?: string };
            if (ticket && body.status) {
                ticket.status = body.status;
            }
            return mockSuccess(ticket ?? TICKETS[0]);
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/]+\/watchers/i,
        handler: (req) => {
            const id = ticketId(req.url) ?? TICKETS[0].id;
            const body = (req.body || {}) as { email?: string; user_id?: number };
            const watcher: Watcher = { id: `w-${Date.now()}`, email: body.email ?? null, user_id: body.user_id ?? null };
            WATCHERS[id] = [...(WATCHERS[id] ?? []), watcher];
            return mockSuccess(watcher);
        },
    },
    {
        method: 'DELETE',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/]+\/watchers\/[^/]+/i,
        handler: (req) => {
            const id = ticketId(req.url);
            const watcherId = req.url.split('/').pop();
            if (id && watcherId) {
                WATCHERS[id] = (WATCHERS[id] ?? []).filter((w) => w.id !== watcherId);
            }
            return mockSuccess({ ok: true });
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets\/[^/]+\/escalate-to-ashcorp/i,
        handler: (req) => {
            const id = ticketId(req.url) ?? TICKETS[0].id;
            const t = findTicket(id);
            if (t) {
                t.status = 'escalated';
                t.handler = 'ashcorp';
            }
            return mockSuccess(t ?? TICKETS[0]);
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/support\/tickets(\?|$)/i,
        handler: (req) => {
            const body = (req.body || {}) as { subject?: string; body?: string };
            const next: Ticket = {
                id: `tkt-${TICKETS.length + 1}`,
                reference: `SUP-${1000 + TICKETS.length + 1}`,
                subject: body.subject || 'New ticket',
                status: 'open',
                priority: 'normal',
                source: 'web',
                handler: 'tenant',
                messages_count: 1,
                created_at: new Date().toISOString(),
            };
            TICKETS.push(next);
            MESSAGES[next.id] = [
                { id: 'm-new', direction: 'in', body_text: body.body || '', vetting_status: 'sent', created_at: next.created_at },
            ];
            return mockSuccess(next);
        },
    },
];
